import { AvailableMetricResponse, getContactResponseTypeName, getTimeOfDayName } from '../../types/api/metrics/request';
import { createSimpleColumn, createPercentColumn, createDateColumn, createMonthColumn, createMappedColumn, createSimpleColumnWithFallback, createTimeColumn } from "../common/TableComponent";
import * as MetricTables from "../../types/api/metrics";
import { MetricType } from "../../types/api/metrics/metricsType";
import { getPlatformName, getButtonName } from '../../types/api/metrics/request';

export const emptyListDescription = `По вашему запросу не найдено
подходящих данных`;

// Общие колонки
const userId = () => createSimpleColumnWithFallback(['userNumber', 'userId'], 'Пользователь ID');
const platformColumn = () => createMappedColumn('platform', 'Платформа', getPlatformName);
const countryColumn = () => createSimpleColumnWithFallback(['country', 'countryTitle'], 'Страна');
const cityColumn = () => createSimpleColumn('city', 'Город');

// Retention Rate
const retentionRateAggregatedColumns = [
    createDateColumn<MetricTables.RetentionRateAggregatedResponse>('dateOfRegistration', 'Дата регистрации', true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.RetentionRateAggregatedResponse>('newUsersCount', 'Кол-во новых пользователей', true),
    createPercentColumn<MetricTables.RetentionRateAggregatedResponse>('d1Retention', 'D1 Retention (%)', true),
    createPercentColumn<MetricTables.RetentionRateAggregatedResponse>('d7Retention', 'D7 Retention (%)', true),
    createPercentColumn<MetricTables.RetentionRateAggregatedResponse>('d30Retention', 'D30 Retention (%)', true)
];

const retentionRateDetailedColumns = [
    userId(),
    platformColumn(),
    countryColumn(),
    cityColumn(),
    createDateColumn<MetricTables.RetentionRateDetailedResponse>('dateOfRegistration', 'Дата регистрации', true),
    createDateColumn<MetricTables.RetentionRateDetailedResponse>('dateOfLastLogin', 'Дата последнего входа', true),
    createSimpleColumn<MetricTables.RetentionRateDetailedResponse>('loginsPerPeriod', 'Кол-во входов за период', true),
    createPercentColumn<MetricTables.RetentionRateDetailedResponse>('retentionRate', 'Retention (%)', true)
];

// Churn Rate
const churnRateAggregatedColumns = [
    createDateColumn<MetricTables.ChurnRateResponse>('period', 'Период', true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.ChurnRateResponse>('totalUsers', 'Кол-во новых пользователей', true),
    createSimpleColumn<MetricTables.ChurnRateResponse>('churnedUsers', 'Кол-во ушедших пользователей', true),
    createSimpleColumn<MetricTables.ChurnRateResponse>('churnRate', 'Churn Rate (%)', true)
];

const churnRateDetailedColumns = [
    userId(),
    platformColumn(),
    countryColumn(),
    cityColumn(),
    createDateColumn<MetricTables.ChurnRateDetailedResponse>('dateOfRegistration', 'Дата регистрации', true),
    createDateColumn<MetricTables.ChurnRateDetailedResponse>('lastActivity', 'Последняя активность', true),
    createSimpleColumn<MetricTables.ChurnRateDetailedResponse>('daysWithoutLogin', 'Дней без входа', true),
    createPercentColumn<MetricTables.ChurnRateDetailedResponse>('churnRate', 'Churn (%)', true)
];

// DAU
const dauColumns = [
    createDateColumn<MetricTables.DauResponse>("date", "Дата", true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.DauResponse>("city", "Город"),
    createSimpleColumn<MetricTables.DauResponse>("dailyActiveUsers", "Активные пользователи", true),
];

const dauDetailedColumns = [
    createDateColumn<MetricTables.DauResponse>('date', 'Дата', true),
    userId(),
    platformColumn(),
    countryColumn(),
    cityColumn(),
    createTimeColumn<MetricTables.DauResponse>('firstLoginTime', 'Время первого входа', true),
    createTimeColumn<MetricTables.DauResponse>('lastLoginTime', 'Время последнего входа', true),
    createSimpleColumn<MetricTables.DauResponse>('sessionsCount', 'Кол-во сессий за день', true)
];

// MAU
const mauColumns = [
    createMonthColumn<MetricTables.MauResponse>("month", "Месяц", true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.MauResponse>("city", "Город"),
    createSimpleColumn<MetricTables.MauResponse>("monthlyActiveUsers", "Активные пользователи", true),
];

const mauDetailedColumns = [
    createMonthColumn<MetricTables.MAUDetailedResponse>('month', 'Месяц', true),
    userId(),
    platformColumn(),
    countryColumn(),
    cityColumn(),
    createDateColumn<MetricTables.MAUDetailedResponse>('firstLoginInMonth', 'Дата первого входа в месяце', true),
    createDateColumn<MetricTables.MAUDetailedResponse>('lastLoginInMonth', 'Дата последнего входа', true),
    createSimpleColumn<MetricTables.MAUDetailedResponse>('activeDaysInMonth', 'Кол-во дней сессий', true)
];

// Daily Sessions per DAU
const dailySessionsDauColumns = [
    createDateColumn<MetricTables.DailySessionsDauResponse>("date", "Дата", true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.DailySessionsDauResponse>("city", "Город"),
    createSimpleColumn<MetricTables.DailySessionsDauResponse>("dailyActiveUsers", "Активные пользователи", true),
    createSimpleColumn<MetricTables.DailySessionsDauResponse>("totalSessions", "Кол-во сессий", true),
    createSimpleColumn<MetricTables.DailySessionsDauResponse>("avgSessionsPerDau", "Среднее сессий на 1 DAU", true),
];

const dailySessionsPerDAUDetailedColumns = [
    userId(),
    platformColumn(),
    countryColumn(),
    createDateColumn<MetricTables.DailySessionsPerDAUDetailedResponse>('date', 'Дата', true),
    createSimpleColumn<MetricTables.DailySessionsPerDAUDetailedResponse>('sessionsCount', 'Кол-во сессий', true)
];

// Stickiness (DAU/MAU)
const stickinessColumns = [
    createMonthColumn<MetricTables.StickinessResponse>('month', 'Месяц', true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.StickinessResponse>('avgDau', 'DAU', true),
    createSimpleColumn<MetricTables.StickinessResponse>('mau', 'MAU', true),
    createPercentColumn<MetricTables.StickinessResponse>('stickiness', 'Stickiness (%)')
];

const stickinessDetailedColumns = [
    userId(),
    platformColumn(),
    countryColumn(),
    cityColumn(),
    createMonthColumn<MetricTables.StickinessDetailedResponse>('month', 'Месяц', true),
    createSimpleColumn<MetricTables.StickinessDetailedResponse>('activeDaysInMonth', 'Дни активности в месяце', true),
    createSimpleColumn<MetricTables.StickinessDetailedResponse>('totalDaysInMonth', 'Всего дней в месяце', true),
    createPercentColumn<MetricTables.StickinessDetailedResponse>('stickiness', 'Stickiness (%)', true)
];

// ROI
const roiColumns = [
    createSimpleColumn<MetricTables.RoiResponse>("marketingCampaign", "Маркетинговая компания", true),
    createSimpleColumn<MetricTables.RoiResponse>("source", "Источник"),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.RoiResponse>("costs", "Расходы ($)", true),
    createSimpleColumn<MetricTables.RoiResponse>("revenue", "Доходы ($)", true),
    createPercentColumn<MetricTables.RoiResponse>("roi", "ROI (%)"),
];

const ctrLevelColumns = [
    createSimpleColumn<MetricTables.CtrLevelResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.CtrLevelResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.CtrLevelResponse>('adsCount', 'Кол-во объявлений', true),
    createSimpleColumn<MetricTables.CtrLevelResponse>('contactsCount', 'Кол-во контактов', true),
    createSimpleColumn<MetricTables.CtrLevelResponse>('demandLevel', 'Уровень спроса', true)
];

// Просмотров на объявление
const viewsPerAdDetailed = [
    userId(),
    createDateColumn<MetricTables.AdViewsDetailedResponse>('dateOfFirstView', 'Дата первого просмотра', true),
    createSimpleColumnWithFallback<MetricTables.AdViewsDetailedResponse>(["adNumber", "adId"], 'ID объявления'),
    createSimpleColumn<MetricTables.AdViewsDetailedResponse>('adTitle', 'Название объявления'),
    createSimpleColumn<MetricTables.AdViewsDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.AdViewsDetailedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.AdViewsDetailedResponse>('viewCount', 'Кол-во просмотров', true),
    platformColumn()
];

const viewsPerAdAggregated = [
    createSimpleColumnWithFallback<MetricTables.AdViewsAgregatedResponse>(["adNumber", "adId"], 'ID объявления'),
    createSimpleColumn<MetricTables.AdViewsAgregatedResponse>('adTitle', 'Заголовок'),
    createSimpleColumn<MetricTables.AdViewsAgregatedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.AdViewsAgregatedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.AdViewsAgregatedResponse>('totalViews', 'Просмотры', true),
    createSimpleColumn<MetricTables.AdViewsAgregatedResponse>('avgViewsPerDay', 'Среднее в день', true),
];

// Просмотры объявлений
const adViewsAggregatedColumns = [
    createSimpleColumn<MetricTables.CategoryViewStatisticsResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.CategoryViewStatisticsResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.CategoryViewStatisticsResponse>('totalAds', 'Кол-во объявлений', true),
    createSimpleColumn<MetricTables.CategoryViewStatisticsResponse>('totalViews', 'Кол-во просмотров', true),
    createSimpleColumn<MetricTables.CategoryViewStatisticsResponse>('avgViewsPerAd', 'Среднее просмотров на 1 объявление', true),
];

// Продавцы
const sellersDetailedColumns = [
    userId(),
    createSimpleColumn<MetricTables.SellersResponse>("adsCount", "Кол-во объявлений", true),
    createSimpleColumn<MetricTables.SellersResponse>("categoryTitle", "Раздел"),
    createSimpleColumn<MetricTables.SellersResponse>("catalogTitle", "Подраздел"),
    createDateColumn<MetricTables.SellersResponse>("publicationDate", "Дата публикации", true),
];

const sellersAgreementColumns = [
    createSimpleColumn<MetricTables.SellersAgreementResponse>("categoryTitle", "Раздел"),
    createSimpleColumn<MetricTables.SellersAgreementResponse>("catalogTitle", "Подраздел"),
    createSimpleColumn<MetricTables.SellersAgreementResponse>("sellersCount", "Кол-во продавцов", true),
    createSimpleColumn<MetricTables.SellersAgreementResponse>("totalAds", "Кол-во объявлений", true),
    createDateColumn<MetricTables.SellersAgreementResponse>("publicationDate", "Дата публикации", true),
];

// Контакты
const userContactsAgreementColumns = [
    createDateColumn<MetricTables.ContactsResponse>("date", "Дата", true),
    platformColumn(),
    countryColumn(),
    createSimpleColumn<MetricTables.ContactsResponse>(
        "uniqueUsersMadeContact",
        "Кол-во уник. пользователей, совершивших контакт",
        true
    ),
    createSimpleColumn<MetricTables.ContactsResponse>("totalContacts", "Кол-во контактов (чат+звонки)", true),
];

const userContactsDetailedColumns = [
    createDateColumn<MetricTables.UserContactsDetailedResponse>('date', 'Дата', true),
    userId(),
    createSimpleColumnWithFallback<MetricTables.UserContactsDetailedResponse>(["recipientNumber", "recipientId"], 'Контактный ID'),
    createMappedColumn<MetricTables.UserContactsDetailedResponse>('contactMethod', 'Тип контакта', getContactResponseTypeName),
    createSimpleColumnWithFallback<MetricTables.UserContactsDetailedResponse>(["adNumber", "adId"], 'Объявление ID'),
    createSimpleColumn<MetricTables.UserContactsDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.UserContactsDetailedResponse>('catalogTitle', 'Подраздел')
];

const allViewsColumns = [
    createDateColumn<MetricTables.AllViewsResponse>("date", "Дата", true),
    createSimpleColumnWithFallback<MetricTables.AllViewsResponse>(["adNumber", "adId"], "ID объявления"),
    createSimpleColumn<MetricTables.AllViewsResponse>("catalogTitle", "Раздел"),
    createSimpleColumn<MetricTables.AllViewsResponse>("categoryTitle", "Подраздел"),
    createSimpleColumn<MetricTables.AllViewsResponse>("viewsCount", "Кол-во просмотров", true),
    createMappedColumn<MetricTables.AllViewsResponse>("timeOfDay", "Время суток", getTimeOfDayName),
];

// Цепочка переходов
const userJourneyAgreementColumns = [
    createSimpleColumn<MetricTables.ChainsResponse>("categoryTitle", "Раздел"),
    createSimpleColumn<MetricTables.ChainsResponse>("catalogTitle", "Подраздел"),
    createSimpleColumn<MetricTables.ChainsResponse>("avgDwellTime", "Среднее время нахождения (сек)", true),
    createSimpleColumn<MetricTables.ChainsResponse>("avgPositionInChain", "Среднее позиция в цепочке", true),
    createSimpleColumn<MetricTables.ChainsResponse>("totalVisits", "Кол-во посещений", true),
    createSimpleColumn<MetricTables.ChainsResponse>("uniqueUsers", "Уникальных пользователей", true),
];

const userJourneyDetailedColumns = [
    createSimpleColumn<MetricTables.UserJourneyDetailedResponse>('sessionId', 'Сессия ID'),
    userId(),
    createSimpleColumn<MetricTables.UserJourneyDetailedResponse>('step', 'Шаг', true),
    createSimpleColumn<MetricTables.UserJourneyDetailedResponse>('screenName', 'Экран'),
    createTimeColumn<MetricTables.UserJourneyDetailedResponse>('entryTime', 'Время входа', true, true),
    createTimeColumn<MetricTables.UserJourneyDetailedResponse>('exitTime', 'Время выхода', true, true),
    createSimpleColumn<MetricTables.UserJourneyDetailedResponse>('timeOnScreen', 'Время на экране (сек.)', true)
];

// Популярные фильтры

const popularFiltersAgreementColumns = [
    createSimpleColumn<MetricTables.FiltersResponse>("filterName", "Фильтр"),
    createSimpleColumn<MetricTables.FiltersResponse>("usageCount", "Кол-во применений", true),
    platformColumn(),
    createSimpleColumn<MetricTables.FiltersResponse>("userType", "Тип пользователя"),
];

const popularFiltersDetailedColumns = [
    userId(),
    createSimpleColumn<MetricTables.FilterUsageDetailedResponse>('userType', 'Тип пользователя'),
    createSimpleColumn<MetricTables.FilterUsageDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.FilterUsageDetailedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.FilterUsageDetailedResponse>('filtersCount', 'Кол-во фильтров', true),
    createSimpleColumn<MetricTables.FilterUsageDetailedResponse>('uniqueFilters', 'Уникальные фильтры'),
    createDateColumn<MetricTables.FilterUsageDetailedResponse>('lastUsageDate', 'Последняя дата использования', true)
];

// Поисковые запросы
const searchAgreementColumns = [
    createDateColumn<MetricTables.SearchResponse>("date", "Дата", true),
    createSimpleColumn<MetricTables.SearchResponse>("searchTerm", "Ключевой запрос", true),
    createSimpleColumn<MetricTables.SearchResponse>("usedHint", "Использована подсказка"),
    createSimpleColumn<MetricTables.SearchResponse>("totalSearches", "Кол-во запросов", true),
];

const searchDetailedColumns = [
    userId(),
    createSimpleColumn<MetricTables.SearchQueriesDetailedResponse>('userType', 'Тип пользователя'),
    createSimpleColumn<MetricTables.SearchQueriesDetailedResponse>('searchTerm', 'Запрос'),
    createSimpleColumn<MetricTables.SearchQueriesDetailedResponse>('usedHint', 'Использование подсказки'),
    createSimpleColumn<MetricTables.SearchQueriesDetailedResponse>('searchesCount', 'Кол-во поисков', true),
    createDateColumn<MetricTables.SearchQueriesDetailedResponse>('lastSearchDate', 'Последняя дата поиска', true)
];

// Пользователи с объявлениями
const usersWithAdsDetailColumns = [
    userId(),
    createDateColumn<MetricTables.UsersWithAdsDetailedResponse>('publicationDate', 'Дата публикации', true),
    createSimpleColumn<MetricTables.UsersWithAdsDetailedResponse>('adsCount', 'Кол-во объявлений', true),
    createSimpleColumn<MetricTables.UsersWithAdsDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.UsersWithAdsDetailedResponse>('catalogTitle', 'Подраздел'),
];

const usersWithAdsAgreementColumns = [
    createDateColumn<MetricTables.UsersWithAdsAgreementResponse>('date', 'Дата'),
    createSimpleColumn<MetricTables.UsersWithAdsAgreementResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.UsersWithAdsAgreementResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.UsersWithAdsAgreementResponse>('uniqueUsers', 'Кол-во пользователей', true),
    createSimpleColumn<MetricTables.UsersWithAdsAgreementResponse>('totalAds', 'Кол-во объявлений', true)
];

const userAdsDetailedColumns = [
    userId(),
    createSimpleColumn<MetricTables.UserAdsDetailedResponse>('adsCount', 'Кол-во объявлений', true),
    createDateColumn<MetricTables.UserAdsDetailedResponse>('publishDate', 'Дата публикации', true),
    createSimpleColumn<MetricTables.UserAdsDetailedResponse>('section', 'Раздел'),
    createSimpleColumn<MetricTables.UserAdsDetailedResponse>('subsection', 'Подраздел')
];

// Пользователи с сообщениями
const usersWithMessagesAgreementColumns = [
    createDateColumn<MetricTables.UsersWithMessagesResponse>('date', 'Дата', true),
    createSimpleColumn<MetricTables.UsersWithMessagesResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.UsersWithMessagesResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.UsersWithMessagesResponse>('totalMessages', 'Кол-во сообщений', true),
    createSimpleColumn<MetricTables.UsersWithMessagesResponse>('uniqueUsers', 'Уникальных пользователей', true)
];

const usersWithMessagesDetailedColumns = [
    createDateColumn<MetricTables.UsersWithMessagesDetailedResponse>('date', 'Дата', true),
    userId(),
    createSimpleColumn<MetricTables.UsersWithMessagesDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.UsersWithMessagesDetailedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumnWithFallback<MetricTables.UsersWithMessagesDetailedResponse>(["adNumber", "adId"], 'Объявление ID'),
    createSimpleColumn<MetricTables.UsersWithMessagesDetailedResponse>('messagesCount', 'Кол-во сообщений', true)
];

// Откуда был переход
const transitionSourceDetailedColumns = [
    createDateColumn<MetricTables.ScreenTransitionStatisticsResponse>('date', 'Дата', true),
    platformColumn(),
    createSimpleColumnWithFallback<MetricTables.ScreenTransitionStatisticsResponse>(['userNumber', 'userId'], 'ID пользователя'),
    createSimpleColumn<MetricTables.ScreenTransitionStatisticsResponse>('sessionId', 'Сессия ID'),
    createSimpleColumn<MetricTables.ScreenTransitionStatisticsResponse>('fromScreenName', 'Исходный экран'),
    createSimpleColumn<MetricTables.ScreenTransitionStatisticsResponse>('toScreenName', 'Целевой экран'),
    createSimpleColumn<MetricTables.ScreenTransitionStatisticsResponse>('actionType', 'Тип действия'),
    createSimpleColumn<MetricTables.ScreenTransitionStatisticsResponse>('transitionsCount', 'Кол-во переходов', true)
];

const activeAdsColumns = [
    createDateColumn<MetricTables.ActiveAdsResponse>('date', 'Дата', true),
    createSimpleColumn<MetricTables.ActiveAdsResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.ActiveAdsResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.ActiveAdsResponse>('activeAdsCount', 'Кол-во активных объявлений', true),
    createSimpleColumn<MetricTables.ActiveAdsResponse>('avgActiveTimeDays', 'Среднее время активности (дни)', true)
];

// Клики на кнопку «Позвонить»
const clickCallAgreementColumns = [
    createSimpleColumn<MetricTables.ClickCallResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.ClickCallResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.ClickCallResponse>('uniqueAds', 'Кол-во объявлений', true),
    createSimpleColumn<MetricTables.ClickCallResponse>('totalClicks', 'Кол-во кликов', true),
    createSimpleColumn<MetricTables.ClickCallResponse>('avgClicksPerAd', 'Среднее кликов на 1 объявление', true)
];

const clickCallDetailedColumns = [
    createDateColumn<MetricTables.AdClicksDetailedResponse>('date', 'Дата', true),
    userId(),
    createSimpleColumnWithFallback<MetricTables.AdClicksDetailedResponse>(["adNumber", "adId"], 'Объявление ID'),
    platformColumn(),
    createSimpleColumn<MetricTables.AdClicksDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.AdClicksDetailedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.AdClicksDetailedResponse>('clicksCount', 'Количество кликов', true)
];

// Время в меню
const timeInMenuAgreementColumns = [
    createSimpleColumn<MetricTables.TimeInMenuResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.TimeInMenuResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.TimeInMenuResponse>('avgTimePerUser', 'Среднее время на пользователя (сек)', true),
    createSimpleColumn<MetricTables.TimeInMenuResponse>('totalTime', 'Общее кол-во пользователей', true)
];

const timeInMenuDetailedColumns = [
    userId(),
    createSimpleColumn<MetricTables.SectionTimeDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.SectionTimeDetailedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<MetricTables.SectionTimeDetailedResponse>('totalTime', 'Общее время (сек.)', true)
];

// Активность в приложении (время)
const activityInappAgreementColumns = [
    createDateColumn<MetricTables.ActivityInAppAggregatedResponse>('date', 'Дата', true),
    platformColumn(),
    createSimpleColumn<MetricTables.ActivityInAppAggregatedResponse>('uniqueUsers', 'Кол-во пользователей', true),
    createSimpleColumn<MetricTables.ActivityInAppAggregatedResponse>('avgSessionsPerUser', 'Среднее кол-во сессий', true),
    createSimpleColumn<MetricTables.ActivityInAppAggregatedResponse>('totalTimeMinutes', 'Общее время (мин)', true),
    createSimpleColumn<MetricTables.ActivityInAppAggregatedResponse>('avgTimePerUserMinutes', 'Ср. время на пользователя (мин)', true)
];

const activityInappDetailedColumns = [
    createDateColumn<MetricTables.ActivityInAppDetailedResponse>('date', 'Дата', true),
    userId(),
    platformColumn(),
    createSimpleColumn<MetricTables.ActivityInAppDetailedResponse>('sessionsCount', 'Кол-во сессий', true),
    createSimpleColumn<MetricTables.ActivityInAppDetailedResponse>('totalTimeMinutes', 'Общее время (мин)', true),
    createSimpleColumn<MetricTables.ActivityInAppDetailedResponse>('avgTimePerSessionMinutes', 'Среднее время сессии (мин)', true)
];

// Уровень спроса 
const adDemandDetailedColumns = [
    createSimpleColumn<MetricTables.AdDemandRateDetailedResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<MetricTables.AdDemandRateDetailedResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumnWithFallback<MetricTables.AdDemandRateDetailedResponse>(["adNumber", "adId"], 'ID объявления'),
    createSimpleColumn<MetricTables.AdDemandRateDetailedResponse>('adTitle', 'Заголовок'),
    createSimpleColumn<MetricTables.AdDemandRateDetailedResponse>('totalViews', 'Просмотры', true),
    createSimpleColumn<MetricTables.AdDemandRateDetailedResponse>('totalCallClicks', 'Клики Позвонить', true),
    createPercentColumn<MetricTables.AdDemandRateDetailedResponse>('demandRateCtr', 'Уровень спроса (CTR)', true)
];
// Клики на главном экране
const buttonClicksAgreementColumns = [
    createDateColumn<MetricTables.ClicksMainScreenResponse>('date', 'Дата', true),
    platformColumn(),
    createMappedColumn<MetricTables.ClicksMainScreenResponse>('buttonName', 'Название кнопки', getButtonName),
    createSimpleColumn<MetricTables.ClicksMainScreenResponse>('uniqueUsers', 'Кол-во уникальных пользователей', true),
    createSimpleColumn<MetricTables.ClicksMainScreenResponse>('totalClicks', 'Общее кол-во нажатий', true),
    createSimpleColumn<MetricTables.ClicksMainScreenResponse>('avgClicksPerUser', 'Ср. нажатий на пользователя', true)
];

const buttonClicksDetailedColumns = [
    createDateColumn<MetricTables.ButtonClicksDetailedResponse>('date', 'Дата', true),
    userId(),
    platformColumn(),
    createMappedColumn<MetricTables.ButtonClicksDetailedResponse>('buttonName', 'Название кнопки', getButtonName),
    createSimpleColumn<MetricTables.ButtonClicksDetailedResponse>('clicksCount', 'Кол-во нажатий', true),
    createTimeColumn<MetricTables.ButtonClicksDetailedResponse>('firstClickTime', 'Время первого нажатия', true),
    createTimeColumn<MetricTables.ButtonClicksDetailedResponse>('lastClickTime', 'Время последнего нажатия', true)
];

// Маски телефонов
const maskPhonesAgreementColumns = [
    createSimpleColumn<MetricTables.MaskPhonesResponse>('phoneMask', 'Маска номера'),
    countryColumn(),
    createSimpleColumn<MetricTables.MaskPhonesResponse>('registrationCount', 'Кол-во регистраций', true)
];

const maskPhonesDetailedColumns = [
    userId(),
    createSimpleColumn<MetricTables.PhoneRegistrationDetailedResponse>('dateOfRegistration', 'Дата регистрации', true),
    createSimpleColumn<MetricTables.PhoneRegistrationDetailedResponse>('phone', 'Телефон'),
    createSimpleColumn<MetricTables.PhoneRegistrationDetailedResponse>('phoneMask', 'Маска номера'),
    createSimpleColumn<MetricTables.PhoneRegistrationDetailedResponse>('country', 'Страна (по маске)')
];

// IP / Геопозиция
const ipGeopositionAgreementColumns = [
    createSimpleColumn<MetricTables.UserIPAggregatedResponse>('regIpAddress', 'IP-адрес'),
    createSimpleColumn<MetricTables.UserIPAggregatedResponse>('cityTitle', 'Город'),
    countryColumn(),
    createSimpleColumn<MetricTables.UserIPAggregatedResponse>('registrationCount', 'Кол-во регистраций', true),
    createSimpleColumn<MetricTables.UserIPAggregatedResponse>('userCount', 'Кол-во уникальных пользователей', true)
];

const ipGeopositionDetailedColumns = [
    createDateColumn<MetricTables.UserIPDetailedResponse>('registrationAt', 'Дата', true),
    userId(),
    createSimpleColumn<MetricTables.UserIPDetailedResponse>('regIpAddress', 'IP-адрес'),
    createSimpleColumn<MetricTables.UserIPDetailedResponse>('countryTitle', 'Страна'),
    createSimpleColumn<MetricTables.UserIPDetailedResponse>('cityTitle', 'Город')
];

// Агрегированный вариант (по географии)
const geopositionCompositeAgreementColumns = [
    createDateColumn<MetricTables.UserRegistrationDetailResponse>('earliestRegistration', 'Дата'),
    createSimpleColumn<MetricTables.SummaryGeoResponse>('countryTitle', 'Страна'),
    //createSimpleColumn<MetricTables.SummaryGeoResponse>('region', 'Регион'),
    createSimpleColumn<MetricTables.SummaryGeoResponse>('cityTitle', 'Город'),
    createSimpleColumn<MetricTables.SummaryGeoResponse>('registrationCount', 'Кол-во регистраций', true)
];

const geopositionCompositeDetailedColumns = [
    createDateColumn<MetricTables.UserGeolocationDetailedResponse>('registrationAt', 'Дата регистрации', true),
    userId(),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('regPlatform', 'Платформа'),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('phoneMask', 'Маска телефона'),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('ipAddress', 'IP-адрес'),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('countryTitle', 'Страна'),
    //createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('region', 'Регион'),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('cityTitle', 'Город'),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('regLat', 'Широта'),
    createSimpleColumn<MetricTables.UserGeolocationDetailedResponse>('regLon', 'Долгота')
];

export const columnsForCurrentMetrics = (metric: AvailableMetricResponse) => {
    const isAggregated = metric.metricType === 'aggregated'
    switch (metric.metricName) {
        case MetricType.retentionRate:
            return isAggregated ? retentionRateAggregatedColumns : retentionRateDetailedColumns
        case MetricType.churnRate:
            return isAggregated ? churnRateAggregatedColumns : churnRateDetailedColumns
        case MetricType.dau:
            return isAggregated ? dauColumns : dauDetailedColumns
        case MetricType.mau:
            return isAggregated ? mauColumns : mauDetailedColumns
        case MetricType.dailySessionsPerDau:
            return isAggregated ? dailySessionsDauColumns : dailySessionsPerDAUDetailedColumns
        case MetricType.stickiness:
            return isAggregated ? stickinessColumns : stickinessDetailedColumns
        case MetricType.demandLevel:
            return ctrLevelColumns
        case MetricType.viewsPerAd:
            return isAggregated ? viewsPerAdAggregated : viewsPerAdDetailed
        case MetricType.totalViews:
            return allViewsColumns
        case MetricType.adViews:
            return adViewsAggregatedColumns
        case MetricType.sellers:
            return isAggregated ? sellersAgreementColumns : sellersDetailedColumns
        case MetricType.contactAttempts:
            return isAggregated ? userContactsAgreementColumns : userContactsDetailedColumns
        case MetricType.usersWithAds:
            return isAggregated ? usersWithAdsAgreementColumns : usersWithAdsDetailColumns
        case MetricType.usersWithMessages:
            return isAggregated ? usersWithMessagesAgreementColumns : usersWithMessagesDetailedColumns
        case MetricType.screenTransitions:
            return isAggregated ? userJourneyAgreementColumns : userJourneyDetailedColumns
        case MetricType.transitionSource:
            return transitionSourceDetailedColumns
        case MetricType.timeInMenu:
            return isAggregated ? timeInMenuAgreementColumns : timeInMenuDetailedColumns
        case MetricType.activityTime:
            return isAggregated ? activityInappAgreementColumns : activityInappDetailedColumns
        case MetricType.buttonClicks:
            return isAggregated ? buttonClicksAgreementColumns : buttonClicksDetailedColumns
        case MetricType.searchQueries:
            return isAggregated ? searchAgreementColumns : searchDetailedColumns
        case MetricType.phoneMasks:
            return isAggregated ? maskPhonesAgreementColumns : maskPhonesDetailedColumns
        case MetricType.ipGeoposition:
            return isAggregated ? ipGeopositionAgreementColumns : ipGeopositionDetailedColumns
        case MetricType.geopositionComposite:
            return isAggregated ? geopositionCompositeAgreementColumns : geopositionCompositeDetailedColumns
        case MetricType.callButtonClicks:
            return isAggregated ? clickCallAgreementColumns : clickCallDetailedColumns
        case MetricType.popularFilters:
            return isAggregated ? popularFiltersAgreementColumns : popularFiltersDetailedColumns
        case MetricType.adDemandRate:
            return adDemandDetailedColumns
        case MetricType.activeAdsCount:
            return activeAdsColumns
        default:
            return [];
    }
}